/**
 * Mail + calendar accounts (spec 2026-10-13-connections.md): types, API calls
 * and the small pure helpers the Connections and Inbox pages share.
 *
 * Secrets go up once and never come back: no response type here carries a
 * password or token.
 */
import { request } from '../api.js';
import type { InboxItem, MessageRow, ProjectFile } from '../lifeos/types.js';

export type MailCalProvider = 'gmail' | 'gcal' | 'imap' | 'msmail' | 'mscal' | 'caldav' | 'ics' | 'signal';

export interface Connector {
  id: string;
  provider: MailCalProvider | string;
  account: string;
  label: string | null;
  sphereKey: string | null;
  writable: boolean;
  status: 'pending' | 'active' | 'error' | 'disabled' | string;
  lastSyncAt: string | null;
  lastError: string | null;
}

export interface ConnectorsList {
  items: Connector[];
  /** Microsoft app registration present on the server. */
  microsoft: boolean;
  /** Google OAuth client present on the server. */
  google: boolean;
  /** Older payload shape (still sent for the Signal row). */
  configured?: { gmail?: boolean; gcal?: boolean; signal?: boolean; signalSend?: boolean };
}

export interface ImapInput {
  label: string;
  host: string;
  port: number;
  secure: boolean;
  user: string;
  password: string;
  sphereKey: string;
}

export interface CaldavInput {
  label: string;
  url?: string;
  user: string;
  password: string;
  sphereKey: string;
  writable: boolean;
}

export interface IcsInput {
  label: string;
  url: string;
  sphereKey: string;
}

export interface ConnectorPatch {
  label?: string;
  sphereKey?: string | null;
  writable?: boolean;
}

const send = (method: string, body: unknown): RequestInit => ({ method, body: JSON.stringify(body) });
const enc = encodeURIComponent;

/** GET /api/connectors, normalised: a server without the new flags falls back to `configured`. */
export async function getConnectors(): Promise<ConnectorsList> {
  const raw = await request<Partial<ConnectorsList>>('/connectors');
  const c = raw.configured;
  return {
    items: (raw.items ?? []).map((a) => ({ ...a, label: a.label ?? null, writable: Boolean(a.writable) })),
    microsoft: Boolean(raw.microsoft),
    google: raw.google ?? Boolean(c?.gmail || c?.gcal),
    configured: c,
  };
}

export async function addImap(body: ImapInput): Promise<unknown> {
  return request('/connectors/imap', send('POST', body));
}
export async function addCaldav(body: CaldavInput): Promise<unknown> {
  return request('/connectors/caldav', send('POST', body));
}
export async function addIcs(body: IcsInput): Promise<unknown> {
  return request('/connectors/ics', send('POST', body));
}
export async function updateConnector(id: string, patch: ConnectorPatch): Promise<unknown> {
  return request(`/connectors/${enc(id)}`, send('PUT', patch));
}
export async function removeConnector(id: string): Promise<unknown> {
  return request(`/connectors/${enc(id)}`, { method: 'DELETE' });
}

/** Where the browser goes to start a Microsoft sign-in (the server answers with a 302). */
export function microsoftStartUrl(sphereKey: string): string {
  return `/api/connectors/microsoft/start?sphere=${enc(sphereKey)}`;
}

export async function saveAttachmentToProject(
  messageId: number,
  index: number,
  body: { projectId: string; name?: string; about?: string },
): Promise<ProjectFile> {
  return (await request<{ file: ProjectFile }>(`/messages/${messageId}/attachments/${index}/project`, send('POST', body))).file;
}

// --- Pure helpers ---------------------------------------------------------------------------------

/** `mail.<domain>` for an address, '' when there is no domain yet. */
export function defaultImapHost(email: string): string {
  const at = email.trim().lastIndexOf('@');
  if (at < 0) return '';
  const domain = email.trim().slice(at + 1).toLowerCase();
  return /^[a-z0-9.-]+\.[a-z]{2,}$/.test(domain) ? `mail.${domain}` : '';
}

const MAIL: ReadonlySet<string> = new Set(['gmail', 'imap', 'msmail']);
const CAL: ReadonlySet<string> = new Set(['gcal', 'mscal', 'caldav', 'ics']);
export const isMail = (c: Connector): boolean => MAIL.has(c.provider);
export const isCalendar = (c: Connector): boolean => CAL.has(c.provider);
/** ICS links are read-only by nature; every other calendar can be written. */
export const canWrite = (c: Connector): boolean => isCalendar(c) && c.provider !== 'ics';

const KIND: Record<string, string> = {
  gmail: 'Gmail',
  gcal: 'Google Calendar',
  imap: 'Email (IMAP)',
  msmail: 'Outlook mail',
  mscal: 'Outlook calendar',
  caldav: 'iCloud calendar',
  ics: 'Calendar link',
};
export function providerName(p: string): string {
  return KIND[p] ?? p;
}

/** The row's status line in plain words. */
export function statusText(c: Connector, ago: (iso: string) => string): string {
  if (c.status === 'error' || (c.lastError && c.status !== 'active')) return 'Problem';
  if (c.status === 'pending') return 'Waiting for first sync';
  if (c.status === 'disabled') return 'Paused';
  return c.lastSyncAt ? `Synced ${ago(c.lastSyncAt)}` : 'Connected';
}

export interface AttachmentInfo {
  index: number;
  name: string;
  mime: string | null;
  size: number | null;
}

/** A message's attachments, whatever shape the connector stored them in. */
export function attachmentsOf(m: Pick<MessageRow, 'attachments'>): AttachmentInfo[] {
  const list = Array.isArray(m.attachments) ? m.attachments : [];
  return list.map((a, index) => {
    const o = a && typeof a === 'object' ? (a as Record<string, unknown>) : {};
    const name = [o.filename, o.name, o.originalName].find((v): v is string => typeof v === 'string' && v.trim() !== '');
    const mime = [o.mimeType, o.mime, o.contentType].find((v): v is string => typeof v === 'string') ?? null;
    const size = [o.size, o.sizeBytes].find((v): v is number => typeof v === 'number') ?? null;
    return { index, name: name ?? (typeof a === 'string' ? a : `Attachment ${index + 1}`), mime, size };
  });
}

/** Contact fields the server adds to inbox/message rows (spec: contactName, contactSphere). */
type WithContact = { contactName?: string | null; contactSphere?: string | null };

/** The matched contact's name, from the row or its message. */
export function contactNameOf(item: InboxItem): string | null {
  const i = item as InboxItem & WithContact;
  const m = item.message as MessageRow & WithContact;
  return item.contact?.name ?? i.contactName ?? m.contactName ?? null;
}

/** The matched contact's job (sphere key). */
export function contactSphereOf(item: InboxItem): string | null {
  const i = item as InboxItem & WithContact;
  const m = item.message as MessageRow & WithContact;
  return i.contactSphere ?? m.contactSphere ?? item.contact?.sphereKey ?? null;
}

/** Full-page navigation, behind an object so tests can watch it (jsdom can't navigate). */
export const nav = {
  go(url: string): void {
    window.location.assign(url);
  },
};
